import { Controller } from '@hotwired/stimulus'
import { sub, startOfDay, startOfMonth, endOfDay, endOfMonth } from 'date-fns'
import { format, toZonedTime, fromZonedTime } from 'date-fns-tz'

export default class extends Controller {
    static targets = ['startInput', 'endInput', 'picker']
    static values = {
        maxDateRangeDays: Number,
        userIanaTimezone: String,
        submitOnApply: { type: Boolean, default: false },
    }

    connect() {
        // jquery+daterangepicker are both global until we switch pipeline off sprockets
        this.dateRangePicker = $(this.pickerTarget)
            .daterangepicker(
                {
                    startDate: this.#convertToTimeZone(
                        this.startInputTarget.value,
                        this.userIanaTimezoneValue,
                    ),
                    endDate: this.#convertToTimeZone(
                        this.endInputTarget.value,
                        this.userIanaTimezoneValue,
                    ),
                    timePicker: true,
                    ranges: this.#getDateRanges(), // IMPORTANT: #injectDynamicDateRanges, #getDateRanges
                    autoUpdateInput: true,
                    cancelButtonClasses: 'btn-secondary',
                    applyButtonClasses: 'btn-success',
                    showDropdowns: true,
                    autoApply: true,
                    locale: {
                        format: 'YYYY-MM-DD hh:mm A',
                    },
                },
                (startDate, endDate) => {
                    // Update the inputs on change
                    this.startInputTarget.value = this.#convertToTimeZoneISO8601(
                        startDate._d,
                        this.userIanaTimezoneValue,
                    )
                    this.endInputTarget.value = this.#convertToTimeZoneISO8601(
                        endDate._d,
                        this.userIanaTimezoneValue,
                    )

                    if (this.submitOnApplyValue) {
                        this.element.closest('form').requestSubmit()
                    }
                },
            )
            .data('daterangepicker')

        this.#injectDynamicDateRanges()
    }

    openCalendar(_event) {
        this.pickerTarget.focus()
    }

    dontSubmitForm(event) {
        event.preventDefault()
    }

    // private
    #convertToTimeZoneISO8601(date, timeZone) {
        const utcDate = fromZonedTime(date, timeZone)

        // 2025-02-24T05:02:00.000Z -> 2025-02-24T05:02:00Z
        return `${utcDate.toISOString().slice(0, 19)}Z`
    }

    #convertToTimeZone(iso8601Date, timeZone) {
        const date = toZonedTime(new Date(iso8601Date), timeZone)
        return format(date, 'yyyy-MM-dd hh:mm a')
    }

    #injectDynamicDateRanges() {
        // jquery+daterangepicker does not handle dynamic dateranges what they do is they take the date in the contructor and
        // then use 'moment' to convert it.
        // We need to use this super duper complex Proxy to make it so when daterangepicker asks for a range via:
        // daterangepicker.ranges['key'][0] we call a function to compute it in time....
        // Please do not change this ever.

        const rangeKeys = Object.entries(this.dateRangePicker.ranges)
        const closureThis = this
        rangeKeys.forEach(([key, _]) => {
            this.dateRangePicker.ranges[key] = new Proxy([], {
                get(_, prop) {
                    return moment(closureThis.#getDateRanges()[key][prop])
                },
            })
        })
    }

    #getDateRanges() {
        // IMPORTANT: This is coupled with '#injectDynamicDateRanges' and daterangepicker.ranges, please be careful
        const timeNow = toZonedTime(new Date(), this.userIanaTimezoneValue)
        const oneHourAgo = sub(timeNow, { hours: 1 })
        const sixHoursAgo = sub(timeNow, { hours: 6 })
        const startOfToday = startOfDay(timeNow)
        const endOfToday = endOfDay(startOfToday)
        const startOfYesterday = sub(startOfToday, { days: 1 })
        const endOfYesterday = endOfDay(startOfYesterday)
        const sixDaysAgo = startOfDay(sub(timeNow, { days: 6 }))
        const twentyNineDaysAgo = startOfDay(sub(timeNow, { days: 29 }))
        const beginningOfThisMonth = startOfMonth(timeNow)
        const endOfThisMonth = endOfMonth(timeNow)
        const beginningOfLastMonth = startOfMonth(sub(timeNow, { months: 1 }))
        const endOfLastMonth = endOfMonth(beginningOfLastMonth)

        return {
            'Last Hour': [oneHourAgo, timeNow],
            'Last 6 hours': [sixHoursAgo, timeNow],
            Today: [startOfToday, endOfToday],
            Yesterday: [startOfYesterday, endOfYesterday],
            // We use -6 because end of today to 6 days ago = 7 full days
            'Last 7 Days': [sixDaysAgo, endOfToday],
            'Last 30 Days': [twentyNineDaysAgo, endOfToday],
            'This Month': [beginningOfThisMonth, endOfThisMonth],
            'Last Month': [beginningOfLastMonth, endOfLastMonth],
        }
    }
}
